import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import {
  VerifiedUser as VerifiedIcon,
  ErrorOutline as ErrorIcon,
  CheckCircle as ActiveIcon,
  Cancel as ExpiredIcon,
} from "@mui/icons-material";
import axios from "../../api/axios";
import styles from "./VerifyUser.module.css";

const VerifyUser = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("user");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const fetchVerification = async () => {
      if (!userId) {
        setError("No user ID provided.");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`/api/verify/${userId}`);
        setUserData(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError("User not found.");
        } else {
          setError("Unable to verify user at this time. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchVerification();
  }, [userId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Box className={styles.container}>
        <Paper className={styles.card} elevation={3}>
          <Box className={styles.loadingBox}>
            <CircularProgress size={48} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Verifying user...
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles.container}>
        <Paper className={styles.card} elevation={3}>
          <Box className={styles.errorBox}>
            <ErrorIcon className={styles.errorIcon} />
            <Typography variant="h5" className={styles.errorTitle}>
              Verification Failed
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {error}
            </Typography>
            <Link to="/" className={styles.homeLink}>
              Return to Home
            </Link>
          </Box>
        </Paper>
      </Box>
    );
  }

  const activeCerts = userData.certifications?.filter(c => c.status === "Active") || [];
  const expiredCerts = userData.certifications?.filter(c => c.status === "Expired") || [];

  return (
    <Box className={styles.container}>
      <Paper className={styles.card} elevation={3}>
        {/* Header */}
        <Box className={styles.header}>
          <VerifiedIcon className={styles.verifiedIcon} />
          <Typography variant="h5" className={styles.headerTitle}>
            Volunteer Verification
          </Typography>
        </Box>

        <Divider />

        {/* User Info */}
        <Box className={styles.userInfo}>
          <Typography variant="h4" className={styles.userName}>
            {userData.name}
          </Typography>

          {userData.flagLine && (
            <Chip
              label={userData.flagLine}
              className={styles.flagChip}
              size="medium"
            />
          )}

          {userData.bgCheckDate && (
            <Box className={styles.infoRow}>
              <Typography variant="body2" color="text.secondary">
                Background Check Date:
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {formatDate(userData.bgCheckDate)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Certifications */}
        {userData.certifications && userData.certifications.length > 0 && (
          <>
            <Divider />
            <Box className={styles.certSection}>
              <Typography variant="h6" className={styles.certTitle}>
                Certifications
              </Typography>

              {activeCerts.length > 0 && (
                <Box className={styles.certGroup}>
                  {activeCerts.map((cert, idx) => (
                    <Box key={`active-${idx}`} className={styles.certRow}>
                      <ActiveIcon className={styles.activeIcon} />
                      <Box className={styles.certInfo}>
                        <Typography variant="body1" fontWeight={500}>
                          {cert.name}
                        </Typography>
                        {cert.expiration && (
                          <Typography variant="caption" color="text.secondary">
                            Expires: {formatDate(cert.expiration)}
                          </Typography>
                        )}
                      </Box>
                      <Chip label="Active" size="small" color="success" variant="outlined" />
                    </Box>
                  ))}
                </Box>
              )}

              {expiredCerts.length > 0 && (
                <Box className={styles.certGroup}>
                  {expiredCerts.map((cert, idx) => (
                    <Box key={`expired-${idx}`} className={styles.certRow}>
                      <ExpiredIcon className={styles.expiredIcon} />
                      <Box className={styles.certInfo}>
                        <Typography variant="body1" fontWeight={500} sx={{ opacity: 0.7 }}>
                          {cert.name}
                        </Typography>
                        {cert.expiration && (
                          <Typography variant="caption" color="text.secondary">
                            Expired: {formatDate(cert.expiration)}
                          </Typography>
                        )}
                      </Box>
                      <Chip label="Expired" size="small" color="error" variant="outlined" />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}

        {/* No certifications */}
        {(!userData.certifications || userData.certifications.length === 0) && (
          <>
            <Divider />
            <Box className={styles.certSection}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No certifications on record.
              </Typography>
            </Box>
          </>
        )}

        {/* Footer */}
        <Divider />
        <Box className={styles.footer}>
          <Typography variant="caption" color="text.secondary">
            Baptists on Mission — Volunteer Verification
          </Typography>
          <Typography variant="caption" color="text.secondary">
            To verify, call 800.395.5102 x5600
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default VerifyUser;
